/**
 * Low-level Voice Live WebSocket session management.
 * Handles: connection lifecycle, audio I/O (mic capture + playback),
 * event dispatch, and function-call routing.
 *
 * This hook is consumed by useVoiceAssessment, not by UI components directly.
 */
import { useCallback, useEffect, useRef } from 'react';

const VL_DEBUG = '[voice-live]';
function vlDebug(msg: string, ...data: unknown[]) {
    console.debug(VL_DEBUG, msg, ...data);
}

const SAMPLE_RATE = 24000;

export type VoiceLiveToolDefinition = Record<string, unknown>;
export type VoiceLiveFunctionArgs = Record<string, unknown>;

export interface VoiceLiveSessionConfig {
    key: string;
    endpoint: string;
    model: string;
    systemPrompt: string;
    tools: VoiceLiveToolDefinition[];
    voice?: string;
}

export interface VoiceLiveCallbacks {
    onSpeechStarted?: () => void;
    onSpeechStopped?: () => void;
    onTranscriptDelta?: (text: string) => void;
    /** Full assistant utterance text when the model finishes that turn (not per audio chunk). */
    onAudioTranscriptDone?: (text: string) => void;
    onTranscriptDone?: (text: string) => void;
    onFunctionCall?: (name: string, callId: string, args: VoiceLiveFunctionArgs) => void;
    onAudioOutputStarted?: () => void;
    onAudioOutputStopped?: () => void;
    onError?: (error: string) => void;
    onSessionCreated?: () => void;
    onDisconnected?: () => void;
}

export function useVoiceLiveSession() {
    const wsRef = useRef<WebSocket | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const callbacksRef = useRef<VoiceLiveCallbacks>({});
    const isConnectedRef = useRef(false);
    const nextPlayTimeRef = useRef(0);
    const playingSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
    const suppressCurrentResponseAudioRef = useRef(false);
    const suppressedResponseIdsRef = useRef<Set<string>>(new Set());
    const controlTokenSuppressionCountRef = useRef<Map<string, number>>(new Map());

    const setCallbacks = useCallback((cbs: VoiceLiveCallbacks) => {
        callbacksRef.current = cbs;
    }, []);

    // --- Helpers ---

    const sendWsEvent = useCallback((event: Record<string, unknown>) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(event));
        }
    }, []);

    /**
     * Return a tool result. By default this also triggers the model's follow-up
     * turn with an inline `instructions` override.
     *
     * Pass `requestFollowUp: false` when the caller has just swapped the session
     * instructions via sendSessionUpdate and needs the next turn to use them:
     * `response.instructions` *replaces* the session instructions for that
     * response, so the default would discard the freshly pushed content. Such
     * callers are responsible for calling requestResponse() themselves.
     */
    const sendFunctionResult = useCallback((
        callId: string,
        result: unknown,
        options?: { requestFollowUp?: boolean },
    ) => {
        suppressCurrentResponseAudioRef.current = false;
        sendWsEvent({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                call_id: callId,
                output: JSON.stringify(result),
            },
        });
        if (options?.requestFollowUp === false) return;
        sendWsEvent({
            type: 'response.create',
            response: {
                instructions: 'Continue in natural spoken language. Do not read or mention function names, tool calls, JSON, code, IDs, or function outputs.',
            },
        });
    }, [sendWsEvent]);

    // --- Audio Playback (PCM16 chunks from Voice Live) ---

    const ensureAudioCtx = useCallback(() => {
        if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
            audioCtxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
        }
        if (audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume();
        }
        return audioCtxRef.current;
    }, []);

    const playAudioChunk = useCallback((base64Audio: string) => {
        if (suppressCurrentResponseAudioRef.current) return;

        const ctx = ensureAudioCtx();

        const raw = atob(base64Audio);
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        const int16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

        const buffer = ctx.createBuffer(1, float32.length, SAMPLE_RATE);
        buffer.copyToChannel(float32, 0);

        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);

        const now = ctx.currentTime;
        const start = Math.max(now + 0.05, nextPlayTimeRef.current);
        playingSourcesRef.current.add(src);
        src.onended = () => {
            playingSourcesRef.current.delete(src);
        };
        src.start(start);
        nextPlayTimeRef.current = start + buffer.duration;
    }, [ensureAudioCtx]);

    const stopAudioPlayback = useCallback(() => {
        playingSourcesRef.current.forEach((src) => {
            try { src.stop(); } catch { /* source may already have ended */ }
        });
        playingSourcesRef.current.clear();
        nextPlayTimeRef.current = 0;
    }, []);

    /**
     * Replace the active session instructions mid-stream. Used by the lesson
     * facilitator to swap per-slide prompts without tearing down the WS.
     * Caller is responsible for cancelling any in-flight response and
     * triggering a fresh response.create afterwards if needed.
     */
    const sendSessionUpdate = useCallback((instructions: string) => {
        suppressCurrentResponseAudioRef.current = false;
        sendWsEvent({
            type: 'session.update',
            session: { instructions },
        });
    }, [sendWsEvent]);

    /** Cancel any in-flight model response and stop local audio playback. */
    const cancelResponse = useCallback(() => {
        sendWsEvent({ type: 'response.cancel' });
        stopAudioPlayback();
        suppressCurrentResponseAudioRef.current = false;
    }, [sendWsEvent, stopAudioPlayback]);

    /** Trigger a fresh model utterance. */
    const requestResponse = useCallback(() => {
        sendWsEvent({ type: 'response.create' });
    }, [sendWsEvent]);

    // --- Microphone Capture (PCM16 → base64 → input_audio_buffer.append) ---

    const startMicCapture = useCallback(async () => {
        if (micStreamRef.current) return;

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                sampleRate: { ideal: SAMPLE_RATE },
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
            },
        });
        micStreamRef.current = stream;

        const ctx = ensureAudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;

        const processor = ctx.createScriptProcessor(4096, 1, 1);
        processor.onaudioprocess = (e) => {
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
            const input = e.inputBuffer.getChannelData(0);
            const int16 = new Int16Array(input.length);
            for (let i = 0; i < input.length; i++) {
                const s = Math.max(-1, Math.min(1, input[i]));
                int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            const base64 = uint8ToBase64(new Uint8Array(int16.buffer));
            sendWsEvent({ type: 'input_audio_buffer.append', audio: base64 });
        };

        source.connect(processor);
        processor.connect(ctx.destination);
        processorRef.current = processor;
        vlDebug('mic capture started');
    }, [ensureAudioCtx, sendWsEvent]);

    const stopMicCapture = useCallback(() => {
        processorRef.current?.disconnect();
        processorRef.current = null;
        sourceRef.current?.disconnect();
        sourceRef.current = null;
        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(t => t.stop());
            micStreamRef.current = null;
        }
        vlDebug('mic capture stopped');
    }, []);

    // --- WebSocket Message Router ---

    const handleMessage = useCallback((evt: MessageEvent) => {
        let data: Record<string, unknown>;
        try { data = JSON.parse(evt.data) as Record<string, unknown>; } catch { return; }
        const cbs = callbacksRef.current;

        const eventType = stringValue(data.type);
        switch (eventType) {
            case 'conversation.item.created':
            case 'response.output_item.added':
            case 'response.output_item.done':
                if (eventContainsFunctionCall(data)) {
                    const responseId = stringValue(data.response_id);
                    if (responseId) suppressedResponseIdsRef.current.add(responseId);
                    stopAudioPlayback();
                    vlDebug('suppressing audio for function call item', data);
                } else {
                    vlDebug(eventType, data);
                }
                break;

            case 'session.created':
                vlDebug('session.created');
                cbs.onSessionCreated?.();
                break;

            case 'session.updated':
                vlDebug('session.updated');
                break;

            case 'input_audio_buffer.speech_started':
                vlDebug('input_audio_buffer.speech_started');
                cbs.onSpeechStarted?.();
                break;

            case 'input_audio_buffer.speech_stopped':
                vlDebug('input_audio_buffer.speech_stopped', data);
                cbs.onSpeechStopped?.();
                break;

            case 'input_audio_buffer.committed':
            case 'input_audio_buffer.cleared':
                vlDebug(eventType, data);
                break;

            case 'conversation.item.input_audio_transcription.completed':
                vlDebug('input_audio_transcription.completed', { transcript: data.transcript });
                cbs.onTranscriptDone?.(stringValue(data.transcript));
                break;

            case 'conversation.item.input_audio_transcription.delta':
                // Per-token user transcription; high-frequency, intentionally not logged.
                break;

            // High-frequency stream events: do not log per chunk. Only the
            // suppression decisions (above and below) are interesting.
            case 'response.audio.delta':
                if (isSuppressedResponse(data, suppressedResponseIdsRef.current)) break;
                if (typeof data.delta === 'string') playAudioChunk(data.delta);
                break;

            // Marks completion of one assistant audio segment; clears suppression
            // when Azure never emits output_audio_buffer.stopped between transcript
            // control tokens and real speech in the same response.
            case 'response.audio.done':
                suppressCurrentResponseAudioRef.current = false;
                vlDebug('response.audio.done', { response_id: data.response_id });
                break;

            case 'response.audio_transcript.delta':
                if (isSuppressedResponse(data, suppressedResponseIdsRef.current)) {
                    // Suppressed transcript chunk for an in-flight blocked response.
                    // Rare enough relative to audio.delta to be worth a debug line.
                    vlDebug('audio_transcript.delta suppressed (response blocked)', {
                        response_id: data.response_id,
                        delta: data.delta,
                    });
                    break;
                }
                if (isIgnorableAudioControlToken(stringValue(data.delta))) {
                    // Drop transcript-only control markers from Azure. These can arrive
                    // in very high volume (including concatenated tokens), so keep logs
                    // summarized per response instead of per token chunk.
                    const responseId = responseIdFromEvent(data) || '__unknown_response__';
                    const nextCount = (controlTokenSuppressionCountRef.current.get(responseId) || 0) + 1;
                    controlTokenSuppressionCountRef.current.set(responseId, nextCount);
                    if (nextCount === 1 || nextCount % 250 === 0) {
                        vlDebug('audio_transcript control tokens suppressed', {
                            response_id: responseId,
                            count: nextCount,
                        });
                    }
                    break;
                }
                if (isToolLeakText(stringValue(data.delta))) {
                    // Mute queued output for leaked tokens; never add response_id
                    // to suppressedResponseIdsRef here — that silenced captions and
                    // audio for the entire rest of the same response.
                    suppressCurrentResponseAudioRef.current = true;
                    stopAudioPlayback();
                    vlDebug('audio_transcript.delta suppressed (tool leak)', {
                        response_id: data.response_id,
                        delta: data.delta,
                    });
                    break;
                }
                // Transcript UI: only skip deltas handled above (control / leak).
                // suppressCurrentResponseAudioRef gates playAudioChunk only.
                cbs.onTranscriptDelta?.(stringValue(data.delta));
                break;

            case 'response.audio_transcript.done': {
                const transcript = audioTranscriptFromDoneEvent(data);
                vlDebug('audio_transcript.done', { transcript });
                const responseId = responseIdFromEvent(data);
                if (responseId) {
                    const suppressedCount = controlTokenSuppressionCountRef.current.get(responseId) || 0;
                    if (suppressedCount > 0) {
                        vlDebug('audio_transcript control-token summary', {
                            response_id: responseId,
                            count: suppressedCount,
                        });
                    }
                    controlTokenSuppressionCountRef.current.delete(responseId);
                }
                if (isSuppressedResponse(data, suppressedResponseIdsRef.current)) break;
                if (transcript) cbs.onAudioTranscriptDone?.(transcript);
                break;
            }

            case 'response.created':
            case 'response.in_progress':
                vlDebug(eventType, { response_id: responseIdFromEvent(data) });
                break;

            case 'output_audio_buffer.started':
                vlDebug('output_audio_buffer.started');
                cbs.onAudioOutputStarted?.();
                break;

            case 'output_audio_buffer.stopped':
                vlDebug('output_audio_buffer.stopped');
                suppressCurrentResponseAudioRef.current = false;
                cbs.onAudioOutputStopped?.();
                break;

            case 'response.done': {
                const responseId = responseIdFromEvent(data);
                vlDebug('response.done', { response_id: responseId, status: (data.response as Record<string, unknown> | undefined)?.status });
                suppressedResponseIdsRef.current.delete(responseId);
                controlTokenSuppressionCountRef.current.delete(responseId);
                suppressCurrentResponseAudioRef.current = false;
                break;
            }

            case 'response.function_call_arguments.delta':
                // Streaming function-call args; usually high-frequency, skip.
                break;

            case 'response.function_call_arguments.done': {
                let args: VoiceLiveFunctionArgs = {};
                try {
                    const parsed = JSON.parse(stringValue(data.arguments) || '{}') as unknown;
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                        args = parsed as VoiceLiveFunctionArgs;
                    }
                } catch { /* empty */ }
                vlDebug('function_call', { name: data.name, callId: data.call_id, args });
                cbs.onFunctionCall?.(stringValue(data.name), stringValue(data.call_id), args);
                break;
            }

            case 'error':
                vlDebug('server error', data.error);
                cbs.onError?.(errorMessageFromEvent(data.error));
                break;

            default:
                vlDebug('unhandled event', { type: eventType, data });
                break;
        }
    }, [playAudioChunk, stopAudioPlayback]);

    // --- Connect / Disconnect ---

    const connect = useCallback(async (config: VoiceLiveSessionConfig) => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        const host = config.endpoint.replace(/^wss?:\/\//, '');
        const wsUrl =
            `wss://${host}/voice-live/realtime` +
            `?api-version=2025-10-01` +
            `&model=${encodeURIComponent(config.model)}` +
            `&api-key=${encodeURIComponent(config.key)}`;

        vlDebug('connecting', { host, model: config.model });

        return new Promise<void>((resolve, reject) => {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                vlDebug('ws open — sending session.update');
                isConnectedRef.current = true;

                ws.send(JSON.stringify({
                    type: 'session.update',
                    session: {
                        modalities: ['text', 'audio'],
                        instructions: config.systemPrompt,
                        voice: {
                            type: 'azure-standard',
                            name: config.voice || 'en-US-DavisNeural',
                        },
                        input_audio_format: 'pcm16',
                        output_audio_format: 'pcm16',
                        input_audio_sampling_rate: SAMPLE_RATE,
                        turn_detection: {
                            type: 'azure_semantic_vad',
                            threshold: 0.5,
                            prefix_padding_ms: 300,
                            silence_duration_ms: 500,
                            interrupt_response: false,
                        },
                        input_audio_transcription: {
                            model: 'azure-speech',
                        },
                        tools: config.tools,
                        tool_choice: 'auto',
                    },
                }));

                // Kick off the first model turn so the AI starts the assessment immediately.
                ws.send(JSON.stringify({ type: 'response.create' }));

                startMicCapture().then(resolve).catch((err) => {
                    vlDebug('mic capture failed on connect', err);
                    callbacksRef.current.onError?.('Microphone access denied');
                    reject(err);
                });
            };

            ws.onmessage = handleMessage;

            ws.onerror = () => {
                vlDebug('ws error');
                callbacksRef.current.onError?.('WebSocket connection error');
                reject(new Error('WebSocket connection error'));
            };

            ws.onclose = (e) => {
                vlDebug('ws closed', { code: e.code, reason: e.reason });
                isConnectedRef.current = false;
                stopMicCapture();
                callbacksRef.current.onDisconnected?.();
            };
        });
    }, [handleMessage, startMicCapture, stopMicCapture]);

    const disconnect = useCallback(() => {
        stopMicCapture();
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
            audioCtxRef.current.close().catch((err) => {
                console.error('Error closing audio context', err);
            });
            audioCtxRef.current = null;
        }
        isConnectedRef.current = false;
        suppressCurrentResponseAudioRef.current = false;
        suppressedResponseIdsRef.current.clear();
        stopAudioPlayback();
        nextPlayTimeRef.current = 0;
        vlDebug('disconnected');
    }, [stopAudioPlayback, stopMicCapture]);

    useEffect(() => () => { disconnect(); }, [disconnect]);

    return {
        connect,
        disconnect,
        sendEvent: sendWsEvent,
        sendFunctionResult,
        sendSessionUpdate,
        cancelResponse,
        requestResponse,
        setCallbacks,
        isConnectedRef,
    };
}

/** Efficiently encode Uint8Array to base64 without spreading into String.fromCharCode (avoids stack overflow on large buffers). */
function uint8ToBase64(bytes: Uint8Array): string {
    const CHUNK = 0x8000;
    const parts: string[] = [];
    for (let i = 0; i < bytes.length; i += CHUNK) {
        parts.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
    }
    return btoa(parts.join(''));
}

function isToolLeakText(text: string): boolean {
    const normalized = text.toLowerCase();
    return (
        normalized.includes('<|ghissue|>') ||
        normalized.includes('<|tool') ||
        normalized.includes('<|function') ||
        normalized.includes('assistant to=functions') ||
        normalized.includes('to=functions') ||
        normalized.includes('functions.') ||
        normalized.includes('function_call') ||
        normalized.includes('tool_call') ||
        normalized.includes('mark_slide_progress') ||
        normalized.includes('complete_lesson_session') ||
        normalized.includes('record_response') ||
        normalized.includes('revisit_question') ||
        normalized.includes('complete_assessment') ||
        // Scripted facilitator tools. Matched on their underscore forms only —
        // the bare words "reframe" and "repeat" are part of the spoken command
        // menu, so the logging tools are named log_reframe / log_repeat.
        normalized.includes('record_slide_response') ||
        normalized.includes('next_slide') ||
        normalized.includes('complete_session') ||
        normalized.includes('log_reframe') ||
        normalized.includes('log_repeat')
    );
}

function isIgnorableAudioControlToken(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) return false;
    // Azure Voice Live emits internal audio markers in transcript deltas,
    // sometimes as one token and sometimes concatenated token runs.
    // Keep this allow-list tight to avoid swallowing other special tokens.
    return /^(?:<\|(audio|vq|hbr|seg|tts)[a-z0-9_]*\|>)+$/i.test(trimmed);
}

function stringValue(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

/** Resolve final transcript text from `response.audio_transcript.done` payloads (shape varies by provider). */
function audioTranscriptFromDoneEvent(data: Record<string, unknown>): string {
    const direct = stringValue(data.transcript);
    if (direct) return direct;
    const item = data.item;
    if (item && typeof item === 'object' && !Array.isArray(item)) {
        const nested = stringValue((item as Record<string, unknown>).transcript);
        if (nested) return nested;
    }
    return '';
}

function errorMessageFromEvent(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string') return message;
    }
    return 'Voice Live error';
}

function eventContainsFunctionCall(event: Record<string, unknown>): boolean {
    if (stringValue(event.type).includes('function_call')) return true;
    if (stringValue(event.item_type) === 'function_call') return true;
    if (stringValue(event.output_item_type) === 'function_call') return true;

    const item = event.item;
    if (item && typeof item === 'object' && !Array.isArray(item)) {
        const itemRecord = item as Record<string, unknown>;
        if (stringValue(itemRecord.type) === 'function_call') return true;
    }

    const outputItem = event.output_item;
    if (outputItem && typeof outputItem === 'object' && !Array.isArray(outputItem)) {
        const outputItemRecord = outputItem as Record<string, unknown>;
        if (stringValue(outputItemRecord.type) === 'function_call') return true;
    }

    return false;
}

function isSuppressedResponse(event: Record<string, unknown>, suppressedResponseIds: Set<string>): boolean {
    const responseId = responseIdFromEvent(event);
    return !!responseId && suppressedResponseIds.has(responseId);
}

function responseIdFromEvent(event: Record<string, unknown>): string {
    const directResponseId = stringValue(event.response_id);
    if (directResponseId) return directResponseId;

    const response = event.response;
    if (response && typeof response === 'object' && !Array.isArray(response)) {
        return stringValue((response as Record<string, unknown>).id);
    }

    return '';
}
